import { useMutation, useQueryClient } from '@tanstack/react-query';

import { tenantApi } from './api/tenant.api';
import { tenantKeys } from './queries';
import type { CatalogKind, MemberSummary, RoleInput } from './types';

export type MemberPatch = Partial<Pick<MemberSummary, 'role' | 'roleId' | 'status' | 'jobTitle' | 'weeklyCapacityHours' | 'availabilityNote' | 'workloadStatus' | 'displayName' | 'departmentId' | 'memberTypeId' | 'contractTypeId'>>;

function useInvalidateTenant() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: tenantKeys.info });
    void client.invalidateQueries({ queryKey: tenantKeys.members });
    void client.invalidateQueries({ queryKey: tenantKeys.invitations });
    void client.invalidateQueries({ queryKey: ['team-workload'] });
    void client.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

export function useUpdateMember() {
  const invalidate = useInvalidateTenant();
  return useMutation({ mutationFn: ({ id, ...body }: { id: string } & MemberPatch) => tenantApi.updateMember(id, body), onSuccess: invalidate });
}

export function useInviteMember() {
  const invalidate = useInvalidateTenant();
  return useMutation({ mutationFn: tenantApi.invite, onSuccess: invalidate });
}

export function useRevokeInvitation() {
  const invalidate = useInvalidateTenant();
  return useMutation({ mutationFn: tenantApi.revoke, onSuccess: invalidate });
}

export function useUpdateTenant() {
  const invalidate = useInvalidateTenant();
  return useMutation({ mutationFn: tenantApi.update, onSuccess: invalidate });
}

function useInvalidateRoles() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: tenantKeys.roles });
    void client.invalidateQueries({ queryKey: tenantKeys.members });
  };
}

export function useSaveRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({ mutationFn: ({ id, ...body }: RoleInput & { id?: string }) => (id ? tenantApi.updateRole(id, body) : tenantApi.createRole(body)), onSuccess: invalidate });
}

export function useDeleteRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({ mutationFn: tenantApi.deleteRole, onSuccess: invalidate });
}

export function useResendInvitation() {
  const invalidate = useInvalidateTenant();
  return useMutation({ mutationFn: tenantApi.resend, onSuccess: invalidate });
}

function useInvalidateJobTitles() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: tenantKeys.jobTitles });
    void client.invalidateQueries({ queryKey: tenantKeys.members });
    void client.invalidateQueries({ queryKey: tenantKeys.invitations });
  };
}

export function useSaveJobTitle() {
  const invalidate = useInvalidateJobTitles();
  return useMutation({ mutationFn: ({ id, name }: { id?: string; name: string }) => (id ? tenantApi.renameJobTitle(id, name) : tenantApi.createJobTitle(name)), onSuccess: invalidate });
}

export function useDeleteJobTitle() {
  const invalidate = useInvalidateJobTitles();
  return useMutation({ mutationFn: tenantApi.deleteJobTitle, onSuccess: invalidate });
}

function useInvalidateDepartments() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: tenantKeys.departments });
    void client.invalidateQueries({ queryKey: tenantKeys.members });
    void client.invalidateQueries({ queryKey: ['insights'] });
  };
}

export function useSaveDepartment() {
  const invalidate = useInvalidateDepartments();
  return useMutation({ mutationFn: ({ id, ...body }: { id?: string; name: string; leadUserId: string }) => (id ? tenantApi.updateDepartment(id, body) : tenantApi.createDepartment(body)), onSuccess: invalidate });
}

export function useDeleteDepartment() {
  const invalidate = useInvalidateDepartments();
  return useMutation({ mutationFn: tenantApi.deleteDepartment, onSuccess: invalidate });
}

function useInvalidateCatalog(kind: CatalogKind) {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: tenantKeys.catalog(kind) });
    void client.invalidateQueries({ queryKey: tenantKeys.members });
  };
}

export function useSaveCatalogEntry(kind: CatalogKind) {
  const invalidate = useInvalidateCatalog(kind);
  return useMutation({ mutationFn: ({ id, name }: { id?: string; name: string }) => (id ? tenantApi.renameCatalogEntry(kind, id, name) : tenantApi.createCatalogEntry(kind, name)), onSuccess: invalidate });
}

export function useDeleteCatalogEntry(kind: CatalogKind) {
  const invalidate = useInvalidateCatalog(kind);
  return useMutation({ mutationFn: (id: string) => tenantApi.deleteCatalogEntry(kind, id), onSuccess: invalidate });
}

export function useSaveWorkingDays() {
  const client = useQueryClient();
  return useMutation({ mutationFn: tenantApi.setWorkingDays, onSuccess: (days) => client.setQueryData(tenantKeys.workingDays, days) });
}

export function useSaveReportPalette() {
  const client = useQueryClient();
  return useMutation({ mutationFn: tenantApi.setReportPalette, onSuccess: (colors) => client.setQueryData(tenantKeys.reportPalette, colors) });
}
